import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { fmt, errText } from "./shared";

const EMPTY = { face_value: "", code: "", pin: "", expiry_date: "", supplier: "", supplier_order_ref: "",
  redemption_url: "", redemption_instructions: "", terms_url: "" };
const INPUT = "w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-200";

function Input({ label, hint, testid, ...props }) {
  return (
    <label className="block">
      <span className="text-[11px] text-slate-500 font-semibold uppercase tracking-widest">{label}</span>
      <input className={`${INPUT} mt-1`} autoComplete="off" spellCheck={false} data-testid={testid} {...props} />
      {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
    </label>
  );
}

function Confirm({ order, form, checked, setChecked }) {
  return (
    <div className="space-y-3" data-testid="gc-fulfil-confirm">
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm space-y-1.5">
        <Row k="Shopper" v={order.shopper_reference} testid="gc-confirm-shopper" />
        <Row k="Brand" v={order.brand} testid="gc-confirm-brand" />
        <Row k="Gift Card Value" v={`${fmt(Math.round(parseFloat(form.face_value) * 100))} CAD`} testid="gc-confirm-value" />
        <Row k="Cashback Reserved" v={`${fmt(order.reserved_minor)} CAD`} testid="gc-confirm-reserved" />
        <Row k="Supplier" v={form.supplier} testid="gc-confirm-supplier" />
      </div>
      <label className="flex items-start gap-2 text-sm text-slate-800 cursor-pointer">
        <Checkbox checked={checked} onCheckedChange={(v) => setChecked(!!v)} data-testid="gc-confirm-checkbox" />
        I confirm that the Gift Card value and Shopper assignment are correct.
      </label>
    </div>
  );
}

function Row({ k, v, testid }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-slate-500">{k}</span>
      <span className="font-semibold text-slate-900" data-testid={testid}>{v}</span>
    </div>
  );
}

export function FulfilDialog({ open, onOpenChange, order, onDone, onChanged }) {
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [step, setStep] = useState("form");
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY, supplier: order?.supplier || "", supplier_order_ref: order?.supplier_order_ref || "" });
      setFile(null); setStep("form"); setChecked(false);
    }
  }, [open, order?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!order) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valueOk = /^\d+(\.\d{1,2})?$/.test(form.face_value.trim());
  const canReview = valueOk && form.supplier.trim().length >= 2
    && (form.code.trim() || form.redemption_url.trim() || file || order.has_file);

  const submit = async () => {
    setBusy(true);
    try {
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/admin/rewards/fulfilment/orders/${order.id}/file`, fd);
        setFile(null);
      }
      const { face_value, ...rest } = form;
      await api.post(`/admin/rewards/fulfilment/orders/${order.id}/fulfil`, {
        ...rest, shopper_reference: order.shopper_reference, brand: order.brand, currency: order.currency,
        face_value_minor: Math.round(parseFloat(face_value) * 100), confirm_assignment: checked,
      });
      toast.success("Gift card assigned to the shopper.");
      onDone?.();
    } catch (e) {
      toast.error(errText(e, "Gift card was not assigned."));
      setChecked(false);
      onChanged?.();
      setStep("form");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="gc-fulfil-dialog" data-clarity-mask="true">
        <DialogHeader>
          <DialogTitle>{step === "form" ? "Fulfil Gift Card" : "Confirm Gift Card Assignment"}</DialogTitle>
          <DialogDescription>
            {step === "form"
              ? `${order.brand} · requested ${fmt(order.value_minor)} for ${order.shopper_reference}. Enter only the fields this card uses.`
              : "Check every detail. Mismatches are blocked and nothing is assigned."}
          </DialogDescription>
        </DialogHeader>
        {step === "form" ? (
          <div className="grid grid-cols-2 gap-3">
            <Input label="Brand" value={order.brand} readOnly testid="gc-fulfil-brand" />
            <Input label="Face value (CAD)" value={form.face_value} onChange={set("face_value")} inputMode="decimal"
                   placeholder="25.00" hint="As printed on the card" testid="gc-fulfil-value" />
            <div className="col-span-2"><Input label="Gift card code / number" value={form.code} onChange={set("code")} testid="gc-fulfil-code" /></div>
            <Input label="PIN (if any)" value={form.pin} onChange={set("pin")} testid="gc-fulfil-pin" />
            <Input label="Expiry (if any)" type="date" value={form.expiry_date} onChange={set("expiry_date")} testid="gc-fulfil-expiry" />
            <Input label="Supplier" value={form.supplier} onChange={set("supplier")} testid="gc-fulfil-supplier" />
            <Input label="Supplier order ref" value={form.supplier_order_ref} onChange={set("supplier_order_ref")} testid="gc-fulfil-supplier-ref" />
            <div className="col-span-2"><Input label="Redemption link (if any)" value={form.redemption_url} onChange={set("redemption_url")} placeholder="https://" testid="gc-fulfil-redemption-url" /></div>
            <div className="col-span-2"><Input label="Terms link (if any)" value={form.terms_url} onChange={set("terms_url")} placeholder="https://" testid="gc-fulfil-terms-url" /></div>
            <label className="col-span-2 block">
              <span className="text-[11px] text-slate-500 font-semibold uppercase tracking-widest">Redemption instructions</span>
              <textarea rows={2} value={form.redemption_instructions} onChange={set("redemption_instructions")}
                        className={`${INPUT} mt-1`} data-testid="gc-fulfil-instructions" />
            </label>
            <label className="col-span-2 block">
              <span className="text-[11px] text-slate-500 font-semibold uppercase tracking-widest">Digital card file (optional)</span>
              <input type="file" accept="image/png,image/jpeg,image/webp,application/pdf" className="block mt-1 text-xs"
                     onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="gc-fulfil-file" />
            </label>
          </div>
        ) : (
          <Confirm order={order} form={form} checked={checked} setChecked={setChecked} />
        )}
        <div className="flex items-center justify-between gap-2 mt-2">
          <span className="text-[11px] text-slate-400 inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Encrypted on save · shown masked afterwards</span>
          <div className="flex gap-2">
            <button onClick={() => (step === "form" ? onOpenChange(false) : setStep("form"))}
                    className="px-4 py-2 rounded-full text-sm border border-slate-200 hover:bg-slate-50" data-testid="gc-fulfil-back">
              {step === "form" ? "Cancel" : "Back"}
            </button>
            {step === "form" ? (
              <button disabled={!canReview} onClick={() => { setChecked(false); setStep("confirm"); }}
                      className="pf-btn-primary px-5 py-2 rounded-full text-sm font-semibold disabled:opacity-50" data-testid="gc-fulfil-review">
                Review assignment
              </button>
            ) : (
              <button disabled={!checked || busy} onClick={submit}
                      className="pf-btn-primary px-5 py-2 rounded-full text-sm font-semibold disabled:opacity-50" data-testid="gc-fulfil-assign">
                {busy ? "Assigning…" : "Assign Gift Card"}
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
